/** Preflight checklist API — the onboarding checklist, and the offboarding one a
 * tenant gets when it is closed (tenant retirement).
 *
 * Hand-authored wrapper (console convention: feature modules define their own
 * types) — except an item's `detail`, whose consumed keys come from the spec
 * (`ConsolePreflightItem.detail`). Endpoints:
 *   /preflight_groups/                             — system-wide group CRUD
 *   /preflight_templates/                          — item templates (tour_operator=null) CRUD
 *   /preflight_actions/                            — registry catalog (create-dialog picker)
 *   /tour_operators/{topId}/preflight_items/       — per-TO items of one checklist (list + PATCH;
 *                                                    `?checklist=offboarding`, onboarding by default)
 *   …/preflight_items/{id}/skip/                   — skip an offboarding item, with a reason
 */
import { apiDelete, apiGet, apiPatch, apiPost, client, ConsoleApiError } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

type S = components['schemas'];

/** An item's `detail`: open per action, with the keys the console consumes typed by the spec. */
export type PreflightItemDetail = S['ConsolePreflightItem']['detail'];
/** RESTECH_PROVIDER_CONFIG `detail.ventrata_key`: the cached probe of the stored Ventrata key. */
export type PreflightVentrataKeyDetail = S['ConsolePreflightVentrataKeyDetail'];
export type PreflightVentrataSupplier = S['ConsolePreflightVentrataSupplier'];
/** NOTIFICATION_SUBSCRIPTIONS `detail.accounts[]`. */
export type PreflightFeedAccount = S['ConsolePreflightNotificationSubscriptionAccount'];
export type PreflightFeedSubscription = PreflightFeedAccount['subscriptions'][number];

/** Per-field messages `configure` (and a `run` carrying values) answer a bad value
 *  with: `400 {"errors": {"<ActionField.key>": "<message>"}}`. Deliberately a
 *  duck-type rather than a widening of `ConsoleApiErrorBody.errors` (a `string[]`
 *  elsewhere) — the batch-config conflict guard sets the same precedent. */
export function fieldErrorsOf(err: unknown): Record<string, string> | null {
  if (!(err instanceof ConsoleApiError) || err.status !== 400) return null;
  const errors = (err.body as { errors?: unknown } | undefined)?.errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null;
  const out: Record<string, string> = {};
  for (const [key, message] of Object.entries(errors as Record<string, unknown>)) {
    if (typeof message === 'string') out[key] = message;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export type PreflightTaskType = 'manual' | 'async' | 'multistep';
/** `skipped` and `superseded` occur on offboarding items only: skipped by ops with a
 *  reason (`items.skip`, kept as `context.skip_reason`), superseded when the tenant was
 *  reopened (`context.previous_status`). A PATCH refuses both (400); the PATCH-able four
 *  are `PREFLIGHT_STATUSES`. */
export type PreflightStatus = 'pending' | 'started' | 'completed' | 'error' | 'skipped' | 'superseded';
/** Which checklist an item belongs to: onboarding (every list's default) or the offboarding
 *  items seeded when a tenant is closed. */
export type PreflightChecklist = S['ConsolePreflightChecklist'];
export type PreflightOwnerRole = 'ops' | 'messaging' | 'infra' | 'app_stores' | 'det' | 'dev' | '';
export type PreflightAuto = 'full' | 'partial' | 'none' | 'already' | 'manual';
/** Which endpoint the console's primary run-style button must call for an item —
 *  `run` ⇒ `run/`, `execute` ⇒ `execute/`, null ⇒ the item has no such button.
 *  See `PreflightItem.run_action`. */
export type PreflightRunAction = 'run' | 'execute' | null;

export interface PreflightStep {
  title: string;
  description: string;
}

// --------------------------------------------------------------------------- groups
export interface PreflightGroup {
  id: number;
  code: string;
  name: string;
  description: string;
  sequence: number;
  item_count: number;
  created_ts: string;
  updated_ts: string;
}

export interface PreflightGroupWrite {
  code?: string;
  name?: string;
  description?: string;
  sequence?: number;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export const groups = {
  list(params: ListParams = {}): Promise<PaginatedResponse<PreflightGroup>> {
    return apiGet(`/preflight_groups/${toQuery({
      page: params.page, page_size: params.pageSize, ordering: params.ordering, search: params.search,
    })}`);
  },
  retrieve(id: number): Promise<PreflightGroup> {
    return apiGet(`/preflight_groups/${id}/`);
  },
  create(body: PreflightGroupWrite): Promise<PreflightGroup> {
    return apiPost('/preflight_groups/', body);
  },
  update(id: number, body: PreflightGroupWrite): Promise<PreflightGroup> {
    return apiPatch(`/preflight_groups/${id}/`, body);
  },
  remove(id: number): Promise<void> {
    return apiDelete(`/preflight_groups/${id}/`);
  },
};

// --------------------------------------------------------------------------- user groups
export interface PreflightUserGroupMember {
  id: number;
  email: string;
  name: string;
}

/** Assignment target — one per owner role; members are system users only. */
export interface PreflightUserGroup {
  id: number;
  owner_role: PreflightOwnerRole;
  name: string;
  description: string;
  member_count: number;
  members: PreflightUserGroupMember[];
  created_ts: string;
  updated_ts: string;
}

export const userGroups = {
  list(params: ListParams = {}): Promise<PaginatedResponse<PreflightUserGroup>> {
    return apiGet(`/preflight_user_groups/${toQuery({
      page: params.page, page_size: params.pageSize, ordering: params.ordering, search: params.search,
    })}`);
  },
  retrieve(id: number): Promise<PreflightUserGroup> {
    return apiGet(`/preflight_user_groups/${id}/`);
  },
  /** Replace the group's membership (system-user ids only; backend rejects tenant users). */
  setMembers(id: number, memberIds: number[]): Promise<PreflightUserGroup> {
    return apiPost(`/preflight_user_groups/${id}/set_members/`, { member_ids: memberIds });
  },
};

// --------------------------------------------------------------------------- templates
export interface PreflightTemplate {
  id: number;
  action: string;
  group: number | null;
  group_code: string;
  group_name: string;
  task_type: PreflightTaskType;
  owner_role: PreflightOwnerRole;
  title: string;
  optional: boolean;
  active: boolean;
  sequence: number;
  steps: PreflightStep[];
  can_auto_run: boolean;
  created_ts: string;
  updated_ts: string;
}

export interface PreflightTemplateWrite {
  action?: string;
  group?: number | null;
  task_type?: PreflightTaskType;
  owner_role?: PreflightOwnerRole;
  title?: string;
  optional?: boolean;
  active?: boolean;
  sequence?: number;
  steps?: PreflightStep[];
}

export interface TemplateListParams extends ListParams {
  group?: number;
  active?: 'true' | 'false';
}

export const templates = {
  list(params: TemplateListParams = {}): Promise<PaginatedResponse<PreflightTemplate>> {
    return apiGet(`/preflight_templates/${toQuery({
      page: params.page, page_size: params.pageSize, ordering: params.ordering,
      search: params.search, group: params.group, active: params.active,
    })}`);
  },
  retrieve(id: number): Promise<PreflightTemplate> {
    return apiGet(`/preflight_templates/${id}/`);
  },
  create(body: PreflightTemplateWrite): Promise<PreflightTemplate> {
    return apiPost('/preflight_templates/', body);
  },
  update(id: number, body: PreflightTemplateWrite): Promise<PreflightTemplate> {
    return apiPatch(`/preflight_templates/${id}/`, body);
  },
  remove(id: number): Promise<void> {
    return apiDelete(`/preflight_templates/${id}/`);
  },
};

// --------------------------------------------------------------------------- action catalog
export interface PreflightActionDef {
  action: string;
  group_code: string;
  task_type: PreflightTaskType;
  owner_role: PreflightOwnerRole;
  default_title: string;
  default_steps: PreflightStep[];
  optional: boolean;
  auto: PreflightAuto;
  /** Setup-wizard taxonomy + execution flags (class-level, tenant-free). */
  input_kind: 'none' | 'optional' | 'required' | 'human_action';
  implemented: boolean;
  manual_execute: boolean;
  autorun_enabled: boolean;
}

export interface PreflightActionCatalog {
  actions: PreflightActionDef[];
  task_types: PreflightTaskType[];
  statuses: PreflightStatus[];
  owner_roles: PreflightOwnerRole[];
}

export function getActionCatalog(): Promise<PreflightActionCatalog> {
  return apiGet('/preflight_actions/');
}

// --------------------------------------------------------------------------- per-TO items
export interface PreflightItemOwner {
  id: number;
  email: string;
  name: string;
}

/** The assignment group (one per owner role) + a capped member preview. */
export interface PreflightAssignedGroup {
  id: number;
  owner_role: PreflightOwnerRole;
  name: string;
  member_count: number;
  members: PreflightItemOwner[];
}

export interface PreflightDependency {
  id: number;
  action: string;
  /** The prerequisite's item title — what the operator calls it. */
  title: string;
}

/** An editable Tour_Operator.config value exposed by an action. */
export interface PreflightActionField {
  key: string;
  label: string;
  type: 'int' | 'text' | 'email' | 'bool' | 'select' | 'list';
  /** Dotted config path this field reads/writes. */
  path: string;
  /** Current value (any JSON type) or null if unset. */
  value: unknown;
  /** Default an auto-run / the wizard would write (any JSON type) or null. */
  default: unknown;
  help: string;
  options: Array<Record<string, unknown>> | null;
  /** True when the action cannot run without a value here (feeds missing_inputs). */
  required: boolean;
  /** Renderer hint for a value whose `type` understates it — e.g. a `source_id`
   *  is a text field but is really a pick from the tenant's import keys. Same
   *  vocabulary as the integrations config editor's `x-widget`; an unknown or
   *  empty value falls back to the plain `type`. */
  widget: string;
  /** Console-relative endpoint feeding a remote autocomplete, for widgets whose
   *  options are too large or too tenant-specific to inline in `options`. */
  options_url: string;
  /** This value is a credential: `value` and `default` arrive masked, and sending
   *  the mask back unchanged leaves the stored value alone. */
  secret: boolean;
}

/** What an item needs from ops before its work can happen (setup-wizard taxonomy). */
export type PreflightInputKind = 'none' | 'optional' | 'required' | 'human_action';

/** One required input not yet provided (item.missing_inputs). */
export interface PreflightMissingInput {
  key: string;
  label: string;
  why: string;
}

/** An unmet, applicable dependency gating run/execute (item.blocked_by). */
export interface PreflightBlockedBy {
  id: number;
  action: string;
  status: PreflightStatus;
  /** The blocking item's title — what the operator calls it. */
  title: string;
}

/** The background task a data-sync item spawned (context.bgtask). */
export interface PreflightBgTaskRef {
  id: string;
  task_type: string;
  spawned_ts?: string;
}

/** A direct link to a third-party dashboard the action interacts with. */
export interface PreflightResource {
  label: string;
  url: string;
  help: string;
}

/** Tenant brief on a per-TO item (null on templates) — for the cross-tenant grid. */
export interface PreflightTenantRef {
  id: number;
  code: string;
  name: string;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface PreflightItem {
  id: number;
  action: string;
  checklist: PreflightChecklist;
  tour_operator: PreflightTenantRef | null;
  group: number | null;
  group_code: string;
  group_name: string;
  task_type: PreflightTaskType;
  owner_role: PreflightOwnerRole;
  owner: PreflightItemOwner | null;
  assigned_group: PreflightAssignedGroup | null;
  title: string;
  optional: boolean;
  sequence: number;
  step: number;
  steps: PreflightStep[];
  rendered_steps: PreflightStep[];
  status: PreflightStatus;
  error_message: string;
  error_detail: unknown | null;
  error_ts: string | null;
  context: Record<string, unknown>;
  depends_on: PreflightDependency[];
  applicable: boolean;
  applicable_reason: string;
  can_auto_run: boolean;
  /** Which run-style button the item actually supports — the server's verdict.
   *  `can_auto_run` above is only a FEASIBILITY marker ("could this ever be
   *  automated?"); the run endpoint is stricter (the platform autorun
   *  kill-switch, the action's own `autorun_enabled`), so a button driven off
   *  feasibility offered a Run that answered 409. `execute` means the action is
   *  manually executable (an explicit ops click, not the unattended sweep);
   *  null means render no run-style button at all. */
  run_action: PreflightRunAction;
  /** One short sentence saying why there is no run-style button (null when
   *  there is one, or when there is nothing worth explaining). */
  run_blocked_reason: string | null;
  /** False when the action's automation is still a documented stub (deferred work). */
  implemented: boolean;
  /** Offboarding items: the action can hold back the release of the tenant's code until the item
   *  is completed or skipped (from the action class; always false on onboarding items). Whether it
   *  blocks right now is the release gate's answer (`TenantLifecycleState.release_blockers`). */
  blocks_release: boolean;
  /** Offboarding items: the release gate re-checks the item's condition live, so a completed item
   *  still blocks while the condition holds and only a skip clears it (from the action class;
   *  false or absent on every other item). Optional in the spec. */
  live_release_check?: boolean;
  /** none / optional / required / human_action — the setup wizard's routing key. */
  input_kind: PreflightInputKind;
  /** True when every required input is present (missing_inputs is empty). */
  ready: boolean;
  missing_inputs: PreflightMissingInput[];
  /** Unmet applicable dependencies; run/execute 409 while non-empty. */
  blocked_by: PreflightBlockedBy[];
  /** Editable config values for inline controls (empty for actions with none). */
  config_fields: PreflightActionField[];
  /** Third-party dashboard links (empty for actions with none). */
  resources: PreflightResource[];
  /** Per-action read-state (current values / links / summaries) rendered by the
   *  action's custom UI (ActionCustomUI); empty for actions with none. */
  detail: PreflightItemDetail;
  created_ts: string;
  updated_ts: string;
}

/** SUPPORT_CONTACTS "Find on website" (`find_social_links`): the item, what the website links to
 *  (`social`: network → profile URL) and what was written (`created` / `updated`: network → the
 *  source that won; `kept`: networks a person's row holds; `legal`: legal URLs filled). */
export interface FindSocialLinksResult {
  item: PreflightItem;
  found: { social: Record<string, string>; terms_and_conditions_url: string; privacy_policy_url: string };
  applied: {
    created: Record<string, string>;
    updated: Record<string, string>;
    kept: string[];
    legal: string[];
  };
  summary: string;
}

export interface PreflightItemPatch {
  status?: PreflightStatus;
  step?: number;
  owner?: number | null;
  context?: Record<string, unknown>;
  error_message?: string;
  error_detail?: unknown | null;
}

// --------------------------------------------------------------------------- action runs
export type PreflightRunMode = 'preview' | 'execute' | 'manual' | 'status_check' | 'configure';
export type PreflightRunStatus = 'running' | 'ok' | 'error';

/** One execution record (the inspectable, secret-redacted run log). */
export interface PreflightActionRun {
  id: number;
  item: number;
  mode: PreflightRunMode;
  status: PreflightRunStatus;
  source: string;
  environment: string;
  actor: PreflightItemOwner | null;
  started_ts: string;
  finished_ts: string | null;
  duration_ms: number | null;
  summary: string;
  result: Record<string, unknown> | null;
  log: string[];
  error_message: string;
  error_detail: unknown | null;
  correlation_id: string;
}

/** Result of POST .../run/ — sync returns the run, async returns enqueued=true, run=null. */
export interface PreflightRunResponse {
  enqueued: boolean;
  run: PreflightActionRun | null;
  /** The configure run recorded first when the request carried values. */
  configure_run?: PreflightActionRun | null;
}

/** Skip reasons run_ready reports per non-dispatched item. */
export type PreflightSkipReason =
  | 'completed'
  | 'started'
  | 'not_applicable'
  | 'not_implemented'
  | 'manual'
  | 'not_ready'
  | 'blocked';

/** Result of POST .../preflight_items/run_ready/ (the wizard's "Run setup"). */
export interface PreflightRunReadyResponse {
  dispatched: Array<{ id: number; action: string }>;
  skipped: Array<{ id: number; action: string; reason: PreflightSkipReason }>;
  armed: boolean;
}

/** Result of POST .../preflight_items/auto_advance/. */
export interface PreflightAutoAdvanceResponse {
  auto_advance: boolean;
  run_actions: string[];
}

/** Filters for the cross-tenant Items grid (GET /preflight_items/). */
export interface AllItemsParams extends ListParams {
  /** One id or a comma list of ids (D1). */
  tenant?: TenantIdsParam;
  /** assigned_group id or owner_role code. */
  userGroup?: string | number;
  /** a member's user id. */
  user?: number;
  status?: PreflightStatus;
  /** checklist (item) group id. */
  itemGroup?: number;
  showCompleted?: boolean;
}

/** A refused skip (409 `ConsolePreflightSkipError`): an item a reopen superseded, an onboarding
 *  item, a tenant that is active again, or an item that is no longer open work (`status` says what
 *  it is now). */
export type PreflightSkipError = S['ConsolePreflightSkipError'];

const SKIP_ERROR_CODES: ReadonlySet<string> = new Set<PreflightSkipError['code']>([
  'superseded',
  'not_offboarding',
  'tenant_not_closed',
  'not_skippable',
]);

/** The 409 body of a refused skip, or null for any other error. */
export function skipErrorOf(e: unknown): PreflightSkipError | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 409) return null;
  const body = e.body as Partial<PreflightSkipError> | undefined;
  return typeof body?.code === 'string' && SKIP_ERROR_CODES.has(body.code) ? (body as PreflightSkipError) : null;
}

export const items = {
  /** Bare (unpaginated) per-TO list of one checklist, ordered by group. Without
   *  `checklist` the backend answers the onboarding checklist (the URL carries no param). */
  list(topId: number, checklist?: PreflightChecklist): Promise<PreflightItem[]> {
    return apiGet(`/tour_operators/${topId}/preflight_items/${toQuery({ checklist })}`);
  },
  /** Paginated cross-tenant list for the Preflight → Items page. */
  listAll(params: AllItemsParams = {}): Promise<PaginatedResponse<PreflightItem>> {
    return apiGet(
      `/preflight_items/${toQuery({
        page: params.page,
        page_size: params.pageSize,
        ordering: params.ordering,
        search: params.search,
        tenant: params.tenant,
        user_group: params.userGroup,
        user: params.user,
        status: params.status,
        item_group: params.itemGroup,
        show_completed: params.showCompleted ? 'true' : undefined,
      })}`,
    );
  },
  retrieve(topId: number, id: number): Promise<PreflightItem> {
    return apiGet(`/tour_operators/${topId}/preflight_items/${id}/`);
  },
  patch(topId: number, id: number, body: PreflightItemPatch): Promise<PreflightItem> {
    return apiPatch(`/tour_operators/${topId}/preflight_items/${id}/`, body);
  },
  /** Dry-run the action (no side effects) → the recorded preview run. */
  preview(topId: number, id: number): Promise<PreflightActionRun> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/preview/`, {});
  },
  /** Execute the action (gated by the autorun kill-switch). Optional `values`
   *  are applied via a configure run first (response carries configure_run). */
  run(topId: number, id: number, values?: Record<string, unknown>): Promise<PreflightRunResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/run/`, values ? { values } : {});
  },
  /** Manually mark complete (records a manual run). */
  complete(topId: number, id: number, note?: string): Promise<PreflightActionRun> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/complete/`, { note: note ?? '' });
  },
  /** Skip an offboarding item of a closed tenant, with a reason (required) → the updated
   *  item, `skipped`. Only open work (pending / started / error) can be skipped; a refusal
   *  answers 409 (`skipErrorOf`), a blank reason 400 `{reason: [...]}`. */
  skip(topId: number, id: number, reason: string): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/skip/`, { reason });
  },
  /** Persist ops-edited field values (overwrite). Manage-gated, not autorun-gated. */
  configure(topId: number, id: number, values: Record<string, unknown>): Promise<PreflightActionRun> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/configure/`, { values });
  },
  /** One masked field's real value. Manage-gated and audited per reveal — see
   *  the endpoint's own note on why these are shown at all. */
  revealField(topId: number, id: number, key: string): Promise<{ key: string; value: unknown }> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/reveal_field/`, { key });
  },
  /** Turn on the messaging channel this action manages → the refreshed item. */
  enableChannel(topId: number, id: number): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/enable_channel/`, {});
  },
  /** The run log for this item (most recent first). */
  listRuns(topId: number, id: number): Promise<PreflightActionRun[]> {
    return apiGet(`/tour_operators/${topId}/preflight_items/${id}/runs/`);
  },
  /** Create/update/delete the operator's support contacts → the updated item. `seenIds`: the
   *  item's `detail.seen_ids` when the editor opened -- only those contacts are deleted for being
   *  left out (one written since survives); without it the backend never deletes a social link. */
  saveContacts(topId: number, id: number, contacts: unknown[], seenIds?: number[]): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_contacts/`, {
      contacts,
      ...(seenIds ? { seen_ids: seenIds } : {}),
    });
  },
  /** "Find on website" (SUPPORT_CONTACTS): scan the operator's website now and write the social
   *  profiles it links to (only rows discovery wrote change) → the item, what was found and done.
   *  An unreadable site is a 400 `website_unreadable` with the reason in `detail`. */
  findSocialLinks(topId: number, id: number): Promise<FindSocialLinksResult> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/find_social_links/`, {});
  },
  /** Replace the operator logo (multipart) → the recorded manual-completion run. */
  uploadLogo(topId: number, id: number, logo: File): Promise<PreflightActionRun> {
    const fd = new FormData();
    fd.append('logo', logo);
    return client
      .post<PreflightActionRun>(`/tour_operators/${topId}/preflight_items/${id}/upload_logo/`, fd, {
        headers: { 'Content-Type': undefined },
      })
      .then((r) => r.data);
  },
  /** Select the Access-Group template group (ACCESS_GROUPS_SETUP) → the updated item. */
  selectTemplate(topId: number, id: number, templateGroupId: number | null): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/select_template/`, {
      template_group_id: templateGroupId,
    });
  },
  /** Materialise the selected Access-Group template group → the recorded run. */
  /** Materialise ACCESS_GROUPS_SETUP; `values` = one-or-more access tags per argument alias. */
  materialize(
    topId: number,
    id: number,
    values: Record<string, (number | string)[]> = {},
  ): Promise<PreflightRunResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/materialize/`, { values });
  },
  /** Save the operator's Auth0 auth methods (staff + client) → the updated item. */
  saveAuthMethods(
    topId: number,
    id: number,
    body: { auth_methods: unknown[]; client_auth_methods: unknown[] },
  ): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_auth_methods/`, body);
  },
  /** Save the configured onboarding team members (TEAM_USERS) → the updated item. */
  saveTeamMembers(topId: number, id: number, members: unknown[]): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_team_members/`, { members });
  },
  /** Create dashboard accounts for the configured team members → the recorded run. */
  createAccounts(topId: number, id: number): Promise<PreflightRunResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/create_accounts/`, {});
  },
  /** Explicitly run an action's EXECUTE (actions with manual_execute) → the recorded run.
   *  Optional `values` are applied via a configure run first (response carries configure_run). */
  execute(topId: number, id: number, values?: Record<string, unknown>): Promise<PreflightRunResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/execute/`, values ? { values } : {});
  },
  /** Deep-merge run arguments into the item's context (the staging channel).
   *  Unlike PATCH `context`, which REPLACES the whole object. */
  stage(topId: number, id: number, contextPatch: Record<string, unknown>): Promise<PreflightItem> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/stage/`, {
      context_patch: contextPatch,
    });
  },
  /** Dispatch every eligible item (the setup wizard's "Run setup"); `arm` persists
   *  the plan so completions cascade via the server-side sweep. */
  runReady(
    topId: number,
    body: { item_ids?: number[] | null; arm?: boolean } = {},
  ): Promise<PreflightRunReadyResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/run_ready/`, body);
  },
  /** Pause/resume the armed setup run's auto-advance sweep. */
  autoAdvance(topId: number, enabled: boolean): Promise<PreflightAutoAdvanceResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/auto_advance/`, { enabled });
  },
  /** Fetch the operator's live Branch.io app config (BRANCHIO_APP_SETUP). */
  fetchBranchConfig(topId: number, id: number): Promise<BranchConfigResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/branch_config/`, {});
  },
  /** Update the operator's Branch.io app config (allowlist-filtered server-side). */
  saveBranchConfig(topId: number, id: number, values: Record<string, unknown>): Promise<BranchConfigResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_branch_config/`, { values });
  },
  // ----------------------------------------------------------------- Firebase (FIREBASE_PROJECT_SETUP)
  /** Live-read the operator's Firebase project/app status. */
  fetchFirebaseStatus(topId: number, id: number): Promise<FirebaseStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/firebase_status/`, {});
  },
  /** List the Firebase/Google Cloud projects the automation account can access (existing-project picker). */
  fetchFirebaseProjects(topId: number, id: number): Promise<FirebaseProjectsResponse> {
    return apiGet(`/tour_operators/${topId}/preflight_items/${id}/firebase_projects/`);
  },
  /** Set/validate the Firebase project (mode existing|auto), enable App Distribution, reconcile. */
  saveFirebaseProject(
    topId: number,
    id: number,
    body: { mode: 'existing' | 'auto'; project_id: string; display_name?: string },
  ): Promise<FirebaseActionResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_firebase_project/`, body);
  },
  /** Create the Android + iOS apps for the current environment (idempotent). */
  createFirebaseApps(
    topId: number,
    id: number,
    body: { package_name?: string; bundle_id?: string; app_store_id?: string },
  ): Promise<FirebaseActionResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/create_firebase_apps/`, body);
  },
  /** Fetch the google-services.json / plist TEXT for the current-env app (console builds the Blob). */
  downloadFirebaseConfig(
    topId: number,
    id: number,
    platform: 'android' | 'ios',
  ): Promise<FirebaseConfigFileResponse> {
    return apiGet(
      `/tour_operators/${topId}/preflight_items/${id}/download_firebase_config/?platform=${encodeURIComponent(platform)}`,
    );
  },
  /** Replace the App Distribution tester list (diff → batchAdd/batchRemove). */
  saveFirebaseTesters(topId: number, id: number, body: { emails: string[] }): Promise<FirebaseActionResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_firebase_testers/`, body);
  },
  /** Reconcile against the live project + recompute all Google item statuses. */
  syncFirebase(topId: number, id: number): Promise<FirebaseActionResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/sync_firebase/`, {});
  },
  // ----------------------------------------------------------------- Google Maps (GMAPS_SDK_KEY_SETUP)
  /** Live-read the Maps SDK key status. */
  fetchGmapsStatus(topId: number, id: number): Promise<GmapsStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/gmaps_status/`, {});
  },
  /** Enable Maps SDK, restrict the auto-created Android key (package + SHA-1), store its value. */
  saveGmapsKey(topId: number, id: number, body: { sha1?: string }): Promise<GmapsActionResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_gmaps_key/`, body);
  },
  /** Restrict the Maps key with the Play app-signing SHA-1 (replaces the debug SHA-1). */
  saveGmapsSha1(topId: number, id: number, body: { sha1: string }): Promise<GmapsSha1Response> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/save_gmaps_sha1/`, body);
  },
  // ------------------------------------------------------- Twilio Conversations (TWILIO_CONVERSATIONS_SERVICE)
  /** Live-read the operator's Twilio Conversations Service (confirm it exists + its config). */
  fetchTwilioConversationsStatus(
    topId: number,
    id: number,
  ): Promise<TwilioConversationsStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_conversations_status/`, {});
  },
  // ------------------------------------------------------------------- DNS CNAME (DNS_CNAME_RECORD)
  /** Live-read the operator CNAME from Route53 (where it currently points). */
  fetchDnsCnameStatus(topId: number, id: number): Promise<DnsCnameStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/dns_cname_status/`, {});
  },
  // ------------------------------------------- Twilio webhooks & push (TWILIO_WEBHOOK_PUSH_CONFIG)
  /** Live-read the Conversations post-event webhook + push notification config. */
  fetchTwilioWebhookStatus(topId: number, id: number): Promise<TwilioWebhookStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_webhook_status/`, {});
  },
  // ---------------------------------------------------- Twilio limits & roles (TWILIO_LIMITS_ROLES)
  /** Live-read the Twilio service member/channel limits + the channel-user role permission. */
  fetchTwilioLimitsStatus(topId: number, id: number): Promise<TwilioLimitsStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_limits_status/`, {});
  },
  // ----------------------------------------------------------- Twilio SMS number (TWILIO_SMS_NUMBER)
  /** Search purchasable SMS numbers (preview before buying). */
  searchTwilioSmsNumbers(
    topId: number,
    id: number,
    body: { country?: string; area_code?: string; contains?: string },
  ): Promise<TwilioSmsSearchResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_sms_search/`, body);
  },
  /** Purchase a chosen SMS number and store it in config.twilio_sms_phone_number. */
  provisionTwilioSmsNumber(
    topId: number,
    id: number,
    body: { phone_number: string },
  ): Promise<TwilioSmsProvisionResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_sms_provision/`, body);
  },
  // ------------------------------------------------- Twilio WhatsApp sender (TWILIO_WHATSAPP_SENDER)
  /** Live-read the operator's WhatsApp sender status. */
  fetchTwilioWhatsappStatus(topId: number, id: number): Promise<TwilioWhatsappStatusResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_whatsapp_status/`, {});
  },
  /** Register a WhatsApp sender via the v2 Senders API. */
  registerTwilioWhatsappSender(
    topId: number,
    id: number,
    body: { phone_number?: string },
  ): Promise<TwilioWhatsappRegisterResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_whatsapp_register/`, body);
  },
  // ------------------------------------------ Twilio APNs push credential (TWILIO_APN_CREDENTIAL)
  /** Upload the APNs cert + private key to Twilio (creates the Push Credential). */
  createTwilioApnCredential(
    topId: number,
    id: number,
    body: { certificate: string; private_key: string; sandbox?: boolean },
  ): Promise<TwilioApnCredentialResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_apn_credential/`, body);
  },
  // ------------------------------------------ Twilio FCM push credential (FIREBASE_FCM_CREDENTIAL)
  /** Create the Twilio FCM push credential from the FCM service account / server key. */
  createTwilioFcmCredential(
    topId: number,
    id: number,
    body: { secret: string },
  ): Promise<TwilioApnCredentialResponse> {
    return apiPost(`/tour_operators/${topId}/preflight_items/${id}/twilio_fcm_credential/`, body);
  },
  // ------------------------------------------------------------ Graphical assets (GRAPHICAL_ASSETS)
  /** Generate the app graphical assets from one uploaded source image (multipart). */
  generateGraphicalAssets(topId: number, id: number, source: File): Promise<GraphicalAssetsResponse> {
    const fd = new FormData();
    fd.append('source', source);
    return client
      .post<GraphicalAssetsResponse>(
        `/tour_operators/${topId}/preflight_items/${id}/generate_graphical_assets/`,
        fd,
        { headers: { 'Content-Type': undefined } },
      )
      .then((r) => r.data);
  },
};

/** Result of a Branch.io app-config fetch/update (see save_branch_config / branch_config). */
export interface BranchConfigResponse {
  ok: boolean;
  reason?: 'no_keys' | 'error';
  detail?: string;
  config?: Record<string, unknown>;
  completion?: Record<string, boolean>;
  editable_fields?: string[];
}

// --------------------------------------------------------------------------- Firebase / Google Maps

/** App identifiers for the current environment (config.firebase.apps[<env>]). */
export interface FirebaseApps {
  android_package_name: string | null;
  ios_bundle_id: string | null;
  ios_app_store_id: string | null;
  android_app_id: string | null;
  ios_app_id: string | null;
}

/** The FIREBASE_PROJECT_SETUP action detail() — live project/app/tester state. */
export interface FirebaseDetail {
  configured: boolean;
  environment: string;
  project_id: string | null;
  project_source: string | null;
  android_package_name_default: string | null;
  /** Conventional bundle id for prefilling the form; the endpoint still needs an
   *  explicit value — creating a Firebase app under a guess is a real write. */
  ios_bundle_id_default: string | null;
  app_display_name: string | null;
  /** Whether org-level automatic project creation is available (projectCreator granted). */
  auto_create_available: boolean;
  apps: FirebaseApps;
  testers: string[];
  /** App Distribution group testers are added to (backend constant, e.g. "Curated Planet"). */
  tester_group_name?: string;
  completion: Record<string, boolean>;
}

/** The GMAPS_SDK_KEY_SETUP action detail() — masked key + restriction state. */
export interface GmapsDetail {
  configured: boolean;
  environment: string;
  project_id: string | null;
  package_name: string | null;
  gate_reason: string | null;
  maps_key_set: boolean;
  maps_key: string | null;
  maps_key_name: string | null;
  debug_sha1: string | null;
  completion: Record<string, boolean>;
}

/** Live-read Firebase status (firebase_status). reason set for a not-yet-ready state. */
export interface FirebaseStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | 'no_project';
  detail: FirebaseDetail;
}

/** A Firebase/Google Cloud project the automation account can access. */
export interface FirebaseProject {
  project_id: string;
  display_name: string;
  project_number: string | null;
}

/** The projects the automation account can access (firebase_projects) for the existing-project picker. */
export interface FirebaseProjectsResponse {
  ok: boolean;
  reason?: string;
  detail?: string;
  projects?: FirebaseProject[];
}

/** Live-read Maps SDK key status (gmaps_status). reason set for not-configured / gated. */
export interface GmapsStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | 'gated';
  detail: GmapsDetail;
}

/** Result of a Firebase mutation (save project / create apps / save testers / sync). */
export interface FirebaseActionResponse {
  ok: boolean;
  reason?: 'not_configured' | 'no_project' | 'error';
  detail?: FirebaseDetail;
  /** Raw reconciliation report — only on save_project + sync. */
  reconcile?: Record<string, unknown>;
  /** Recomputed status per Google preflight action code. */
  statuses?: Record<string, string>;
}

/** Result of the Maps key setup (save_gmaps_key). */
export interface GmapsActionResponse {
  ok: boolean;
  reason?: 'not_configured' | 'no_android_app' | 'no_key' | 'error';
  detail?: GmapsDetail;
  statuses?: Record<string, string>;
}

/** The GMAPS_SHA1_FOLLOWUP detail() — the Maps key + the applied Play app-signing SHA-1. */
export interface GmapsSha1Detail {
  configured: boolean;
  environment?: string;
  project_id?: string | null;
  package_name?: string | null;
  gate_reason?: string;
  maps_key_name?: string | null;
  debug_sha1?: string;
  play_sha1?: string | null;
}

/** Result of applying the Play SHA-1 (save_gmaps_sha1). */
export interface GmapsSha1Response {
  ok: boolean;
  reason?: 'not_configured' | 'no_key' | 'error' | null;
  detail?: GmapsSha1Detail;
}

// --------------------------------------------------------------------------- Jira requests

/** detail() for the Jira-request actions (Android/iOS build, push cert). */
export interface JiraRequestDetail {
  configured: boolean;
  assignees?: string[];
  jira?: { key?: string | null; url?: string | null } | null;
  kind?: string;
}

/** The google-services.json / plist file text (download_firebase_config), console builds the Blob. */
export interface FirebaseConfigFileResponse {
  ok: boolean;
  reason?: 'not_configured' | 'no_project' | 'error';
  detail?: string;
  filename?: string;
  content_type?: string;
  content?: string;
}

// --------------------------------------------------------------------------- Twilio Conversations

/** The TWILIO_CONVERSATIONS_SERVICE detail() seed + the live twilio_conversations_status probe. */
export interface TwilioConversationsDetail {
  configured: boolean;
  service_sid: string | null;
  service_set?: boolean;
  service_present?: boolean;
  friendly_name?: string | null;
  date_created?: string | null;
  reachability_enabled?: boolean | null;
  error?: string | null;
}

/** Live status of the operator's Twilio Conversations Service (twilio_conversations_status). */
export interface TwilioConversationsStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | 'error' | null;
  detail: TwilioConversationsDetail;
}

// --------------------------------------------------------------------------- DNS CNAME

/** The DNS_CNAME_RECORD detail() seed + the live dns_cname_status probe. */
export interface DnsCnameDetail {
  configured: boolean;
  hostname: string;
  target: string;
  exists?: boolean;
  current_target?: string | null;
  matches?: boolean;
}

/** Live operator-CNAME status from Route53 (dns_cname_status). */
export interface DnsCnameStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | null;
  detail: DnsCnameDetail;
}

// --------------------------------------------------------------------------- Twilio webhooks & push

/** The TWILIO_WEBHOOK_PUSH_CONFIG detail() seed + the live twilio_webhook_status probe. */
export interface TwilioWebhookDetail {
  configured: boolean;
  service_sid?: string | null;
  target_webhook_url: string;
  post_webhook_url?: string | null;
  method?: string | null;
  filters?: string[];
  filters_ok?: boolean;
  webhook_ok?: boolean;
  push?: Record<string, unknown>;
  error?: string | null;
}

/** Live Conversations webhook + push status (twilio_webhook_status). */
export interface TwilioWebhookStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | 'error' | null;
  detail: TwilioWebhookDetail;
}

// --------------------------------------------------------------------------- Twilio limits & roles

/** The TWILIO_LIMITS_ROLES detail() seed + the live twilio_limits_status probe. */
export interface TwilioLimitsDetail {
  configured: boolean;
  target_limit?: number;
  service_sid?: string | null;
  limits_channel_members?: number | null;
  limits_user_channels?: number | null;
  limits_ok?: boolean;
  channel_user_role_sid?: string | null;
  channel_user_permissions?: string[];
  edit_attrs_ok?: boolean;
  error?: string | null;
}

/** Live Twilio limits + roles status (twilio_limits_status). */
export interface TwilioLimitsStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | 'error' | null;
  detail: TwilioLimitsDetail;
}

// --------------------------------------------------------------------------- Twilio SMS number

/** The TWILIO_SMS_NUMBER detail() — the operator's current SMS from-number. */
export interface TwilioSmsDetail {
  configured: boolean;
  sms_number?: string | null;
  platform_default?: string;
}

/** A purchasable number from the search (preview before buying). */
export interface TwilioSmsNumberOption {
  phone_number: string;
  friendly_name?: string | null;
  locality?: string | null;
  region?: string | null;
  iso_country?: string | null;
}

/** SMS-number search result (twilio_sms_search). */
export interface TwilioSmsSearchResponse {
  ok: boolean;
  reason?: 'not_configured' | 'error' | null;
  detail?: string | null;
  numbers?: TwilioSmsNumberOption[];
}

/** Purchase result (twilio_sms_provision). */
export interface TwilioSmsProvisionResponse {
  ok: boolean;
  reason?: 'not_configured' | 'error' | null;
  detail?: TwilioSmsDetail | string;
  sid?: string | null;
}

// --------------------------------------------------------------------------- Twilio WhatsApp sender

/** The TWILIO_WHATSAPP_SENDER detail() — effective WhatsApp number + sender status. */
export interface TwilioWhatsappDetail {
  configured: boolean;
  waba_configured?: boolean;
  whatsapp_number?: string | null;
  uses_shared_default?: boolean;
  sender_sid?: string | null;
  sender_status?: string | null;
  sms_number?: string | null;
}

/** Live WhatsApp sender status (twilio_whatsapp_status). */
export interface TwilioWhatsappStatusResponse {
  ok: boolean;
  reason?: 'not_configured' | null;
  detail: TwilioWhatsappDetail;
}

/** Registration result (twilio_whatsapp_register). */
export interface TwilioWhatsappRegisterResponse {
  ok: boolean;
  reason?: 'not_configured' | 'no_waba' | 'error' | null;
  detail?: TwilioWhatsappDetail | string;
  sid?: string | null;
  status?: string | null;
}

// --------------------------------------------------------------------------- Twilio APNs push credential

/** The TWILIO_APN_CREDENTIAL detail() — the operator's push credential SIDs. */
export interface TwilioApnDetail {
  configured: boolean;
  apn_credential_sid?: string | null;
  fcm_credential_sid?: string | null;
}

/** APNs credential creation result (twilio_apn_credential). */
export interface TwilioApnCredentialResponse {
  ok: boolean;
  reason?: 'not_configured' | 'error' | null;
  detail?: TwilioApnDetail | string;
  sid?: string | null;
}

// --------------------------------------------------------------------------- Graphical assets

/** A generated app asset published to S3. */
export interface GraphicalAsset {
  name: string;
  key: string;
  url?: string | null;
  width: number;
  height: number;
}

/** The GRAPHICAL_ASSETS detail() — the generated asset set. */
export interface GraphicalAssetsDetail {
  generated?: boolean;
  assets?: GraphicalAsset[];
}

/** Asset generation result (generate_graphical_assets). */
export interface GraphicalAssetsResponse {
  ok: boolean;
  reason?: 'error' | null;
  detail?: GraphicalAssetsDetail | string;
  assets?: GraphicalAsset[];
}

// --------------------------------------------------------------------------- onboarding profile
/** The wizard's branching feature answers (config.onboarding) and has_app (the
 *  Tour_Operator.has_app column: null inherits — a licensee from its parent, a
 *  sandbox from its live operator).
 *  GET reads franchise-aware with the create wizard's defaults filled in; PATCH
 *  (all fields optional) writes only the provided answers — applicability
 *  recomputes live, so invalidate the items query after a save. */
export interface OnboardingProfile {
  /** The onboarding profile (`config.onboarding.profile`). The console never sends it: ops do not pick
   *  it here, and the sandbox profiles are set by sandbox provisioning (metaplan F6, M11) — the editor
   *  refuses to move a tenant off one. */
  profile?: 'standard' | 'standard-no-app' | 'sandbox' | 'partner_sandbox';
  restech: 'none' | 'fareharbor' | 'ventrata' | 'bokun' | 'rezdy' | 'trekksoft' | 'xml' | 'other';
  channels: Array<'sms' | 'whatsapp' | 'push'>;
  cadence: 'day' | 'multi_day';
  guides_split_chat: boolean;
  vehicle_tracking: boolean;
  own_corporate_email: boolean;
  app_store_account: 'curated_planet' | 'operator';
  has_existing_apps: boolean;
  tag_category_set: number | null;
  ag_template_group: number | null;
  /** The effective answer: the column, or — when it is null — the answer it inherits. */
  has_app: boolean;
  /** The column itself: null = inherited (from `has_app_inherits_from`). */
  has_app_own: boolean | null;
  /** Whom a null `has_app_own` inherits from (a licensee's parent, a sandbox's live operator), with
   *  that operator's answer; null when the tenant cannot inherit (its answer is always its own). */
  has_app_inherits_from: HasAppUpstream | null;
  /** Reviews on (D39; default on) — selects the reviews pack, REVIEW_CONFIG and the survey cron gate. */
  reviews_enabled: boolean;
  /** Operator inbox for the negative-review alert; '' when unset. */
  reviews_alert_email: string;
  /** Read-only setup-run state ({auto_advance, run_actions}); write via items.runReady/autoAdvance. */
  setup: { auto_advance?: boolean; run_actions?: string[] };
  /** Read-only go-live state for the Run board's launch card (ignored on PATCH). */
  launch_state: LaunchState;
}

export interface HasAppUpstream {
  id: number;
  code: string;
  name: string;
  has_app: boolean;
}

/** A launch policy as the go-live card shows it (flags keyed by field name,
 *  e.g. incoming_webhooks / chat_enabled / outgoing_sms). */
export interface LaunchPolicyRef {
  id: number;
  name: string;
  flags: Record<string, boolean>;
}

export interface LaunchStatePolicy extends LaunchPolicyRef {
  /** The tenant's default_launch_policy — what tours without their own policy inherit. */
  is_default: boolean;
  /** Tours with this policy set explicitly (deleted tours excluded). */
  tours_count: number;
  /** tours_count plus, for the default, the tours inheriting it. */
  effective_tours_count: number;
}

/** The three go-live switches together: launch policy (THE manual switch —
 *  Draft drops incoming bookings on both the webhook and the backfill path),
 *  the restech send flags (NOTIFICATIONS_SOFT_LAUNCH) and the canned
 *  notification enabled state. Tours are moved with the tours grid's
 *  `set_launch_policy` batch op; the tenant default via tourOperators.update. */
export interface LaunchState {
  enable_launch_policies: boolean;
  default_launch_policy: LaunchPolicyRef | null;
  /** Launch_Policy.check_flag(default, 'incoming_webhooks') — the BOOKING_BACKFILL gate. */
  incoming_bookings_admitted: boolean;
  incoming_bookings_block_reason: string | null;
  /** Stage order: fewest flags first (Draft → Soft launched → Hard launched). */
  policies: LaunchStatePolicy[];
  tours_total: number;
  tours_inheriting_default: number;
  restech: {
    provider: string | null;
    send_invitations_webhook: boolean | null;
    send_surveys: boolean | null;
  };
  notifications: { canned_total: number; enabled: number; disabled: number };
}

export type OnboardingProfilePatch = Partial<
  Omit<
    OnboardingProfile,
    'setup' | 'launch_state' | 'profile' | 'has_app' | 'has_app_own' | 'has_app_inherits_from'
  >
> & {
  /** true / false, or null to inherit again (licensees and sandboxes only — 400 otherwise). */
  has_app?: boolean | null;
};

export const onboardingProfile = {
  get(topId: number): Promise<OnboardingProfile> {
    return apiGet(`/tour_operators/${topId}/onboarding_profile/`);
  },
  patch(topId: number, body: OnboardingProfilePatch): Promise<OnboardingProfile> {
    return apiPatch(`/tour_operators/${topId}/onboarding_profile/`, body);
  },
};
